import { useLayoutEffect, useRef } from 'react'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { Button, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  MESSAGE_TEAM_CHIP,
  SUGGESTION_CHIPS,
  VIEW_MESSAGES_CHIP,
} from '@/features/adviser-chat/lib/constants'
import { useHavenMessagesView } from '@/features/adviser-chat/hooks/useHavenMessagesView'
import {
  HAVEN_LAUNCH_ACTION,
  type HavenLaunchAction,
} from '@/features/adviser-chat/lib/types'

interface SuggestionListProps {
  onSuggestionClick: (message: string) => void
  onLaunchAction?: (action: HavenLaunchAction) => void
}

export const SuggestionList = ({
  onSuggestionClick,
  onLaunchAction,
}: SuggestionListProps) => {
  const messagesViewEnabled = useHavenMessagesView()
  const chips = messagesViewEnabled
    ? SUGGESTION_CHIPS
    : SUGGESTION_CHIPS.filter(
        (chip) => chip !== VIEW_MESSAGES_CHIP && chip !== MESSAGE_TEAM_CHIP
      )

  const listRef = useRef<HTMLUListElement>(null)

  // Rows wrapped into the clipped column must not take keyboard focus.
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return undefined
    const markClipped = () => {
      const firstColumn = list.getBoundingClientRect().left
      Array.from(list.children).forEach((row) => {
        if (row instanceof HTMLElement) {
          row.inert = row.getBoundingClientRect().left > firstColumn + 1
        }
      })
    }
    markClipped()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(markClipped)
    observer.observe(list)
    return () => observer.disconnect()
  }, [chips.length])

  const handleClick = (chip: string) => {
    if (chip === VIEW_MESSAGES_CHIP && onLaunchAction) {
      onLaunchAction(HAVEN_LAUNCH_ACTION.openTickets)
      return
    }
    onSuggestionClick(chip)
  }

  return (
    <ul
      ref={listRef}
      className="flex min-h-0 flex-col flex-wrap overflow-hidden"
    >
      {chips.map((chip, i) => (
        <li
          key={chip}
          className="w-full shrink-0 animate-fade-in-up border-b border-neutral-100 opacity-0 last:border-b-0"
          style={{
            animationDelay: `${(i + 1) * 150}ms`,
            animationFillMode: 'forwards',
          }}
        >
          <Button
            variant="ghost"
            iconRight={faArrowRight}
            onClick={() => handleClick(chip)}
            className="group h-auto min-h-10 w-full justify-between whitespace-normal rounded-none bg-neutral-0 px-4 py-2 text-left hover:bg-neutral-25 [&_svg]:text-neutral-400"
          >
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-neutral-600 group-hover:text-neutral-800"
            >
              {chip}
            </Text>
          </Button>
        </li>
      ))}
    </ul>
  )
}
